'use client';

import CookieYesScript from '@/components/common/consent/cookieyes_script';
import { CONSENT_CATEGORY_LIST, createDefaultConsentState } from '@/lib/consent/categories';
import { getIntegrationCategory } from '@/lib/consent/integration-map';
import { isCookieYesConfigured, readCookieYesConsent, subscribeToCookieYes } from '@/lib/consent/cookieyes-adapter';
import { createContext, useContext, useEffect, useState } from 'react';

const defaultState = createDefaultConsentState();

const ConsentContext = createContext({
    consent: defaultState,
    isConsentReady: false,
    isCmpConfigured: false,
    canUseCategory: () => false,
    canUseIntegration: () => false,
    refreshConsent: () => {}
});

export function ConsentProvider({ children }) {
    const [consent, setConsent] = useState(defaultState);
    const isCmpConfigured = isCookieYesConfigured();

    useEffect(() => {
        const syncConsent = () => {
            const nextConsent = readCookieYesConsent();
            setConsent((previousConsent) => {
                const hasChanged = CONSENT_CATEGORY_LIST.some((categoryName) => previousConsent[categoryName] !== nextConsent[categoryName])
                    || previousConsent.ready !== nextConsent.ready
                    || previousConsent.source !== nextConsent.source;

                return hasChanged ? nextConsent : previousConsent;
            });
        };

        syncConsent();

        const unsubscribe = subscribeToCookieYes(syncConsent);
        return unsubscribe;
    }, []);

    const context = {
        consent,
        isConsentReady: consent.ready,
        isCmpConfigured,
        canUseCategory: (categoryName) => {
            if (categoryName === 'necessary') return true;
            return !!consent[categoryName];
        },
        canUseIntegration: (integrationName) => {
            const categoryName = getIntegrationCategory(integrationName);
            if (categoryName === 'necessary') return true;
            return !!consent[categoryName];
        },
        refreshConsent: () => setConsent(readCookieYesConsent())
    };

    return <ConsentContext.Provider value={context}>
        <CookieYesScript />
        {children}
    </ConsentContext.Provider>;
}

export function useConsent() {
    return useContext(ConsentContext);
}

export default ConsentContext;